import styled from "styled-components";
import media from "helpers/media";

export interface TOCItemProps {
  active?: boolean;
}

export interface StatusDotProps {
  required?: boolean;
}

export interface LegendDotProps {
  required?: boolean;
}

export interface StatusIndicatorInTableProps {
  required?: boolean;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 40px;
  background-color: #f8f9fa;
  min-height: 100vh;
  margin: 0 auto;
`;

export const Header = styled.div`
  display: flex;
  align-self: flex-start;
  align-items: center;
  justify-content: flex-start;
  font-size: 1.6rem;
  gap: 1rem;
  margin-left: 40px;
  border-bottom: 2px solid transparent;
  cursor: pointer;

  max-width: 100%;

  ${media.MD`
    width: 100%;
    padding: 1rem;
    gap: 1rem;
  `}

  &:hover {
    border-bottom: 2px solid #333333;
  }
`;

export const MainContent = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;

  max-width: 100%;
`;

export const Sidebar = styled.div`
  width: 250px;
  flex-shrink: 0;

  ${media.MD`
    display: none;
  `}
`;

export const TableOfContentsCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const TOCTitle = styled.h3`
  font-family: "Roboto Slab", serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  color: #59626c;
  margin: 0;
`;

export const TOCList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const TOCItem = styled.a<TOCItemProps>`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.17;
  color: ${(props) => (props.active ? "#1B75BB" : "#333333")};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #1b75bb;
  }
`;

export const ContentArea = styled.div`
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 40px 32px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 33px;

  ${media.MD`
    width: 100%;
    padding: 1rem;
  `}
`;

export const CareerTitle = styled.h1`
  font-family: "Roboto Slab", serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 0.75;
  letter-spacing: 0.94%;
  color: #333333;
  margin: 0;
`;

export const SectionTitle = styled.h2`
  font-family: "Roboto Slab", serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 2.4rem;
  color: #333333;
  margin: 0;
  margin-bottom: 2.4rem;
`;

export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const Description = styled.p`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.33;
  letter-spacing: 2.22%;
  color: #333333;
  margin: 0;
  text-align: justify;
  max-width: 100%;

  ${media.MD`
    width: 100%;
    font-size: 1.6rem;
    line-height: 1.5;
    letter-spacing: 0.02em;
  `}
`;

export const VideoPlaceholder = styled.div`
  width: 100%;
  height: 460px;
  background: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 18px;
  font-family: "Roboto", sans-serif;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

export const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0; // As per Figma design
`;

export const FilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 12px;
  background: white;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
`;

export const StatusIndicator = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StatusDot = styled.div<StatusDotProps>`
  width: 12.8px;
  height: 12.8px;
  border-radius: 50%;
  background: ${(props) => (props.required ? "#C94780" : "#F3CB3C")};
`;

export const FilterText = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.23;
  letter-spacing: 3.08%;
  color: #59626c;
`;

export const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 33px;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendDot = styled.div<LegendDotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.required ? "#C94780" : "#ECB51B")};
`;

export const LegendText = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.17;
  color: #333333;
`;

export const SubjectTable = styled.div`
  width: 100%;
  height: 159px;
  position: relative;
`;

export const TableHeader = styled.div`
  width: 100%;
  height: 40px;
  background: #e8f4fa;
  display: flex;
  position: relative;
`;

export const TableCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 8px 2px;
  border-right: 0.5px solid #6da7d3;

  &:last-child {
    border-right: none;
  }
`;

export const SubjectName = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.17;
  color: #333333;
  text-align: center;
`;

export const StatusRow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: #f3f5fb;
  display: flex;
  align-items: center;
`;

export const StatusIndicatorInTable = styled.div<StatusIndicatorInTableProps>`
  position: absolute;
  width: 16px;
  height: 16px;
  top: 12px;
  background: ${(props) => (props.required ? "#C94780" : "#ECB51B")};
  border-radius: 50%;
`;

export const Divider = styled.hr`
  width: 1px;
  height: 100%;
  background: #e7ecee;
  border: none;
  margin: 0;

  ${media.MD`
    display: none;
  `}
`;

export const SideCardsContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  margin-top: 24px;
  max-width: 100%;
  ${media.MD`
    flex-direction: column;
  `}
`;

export const DividerVertical = styled.div`
  width: 1px;
  height: 100%;
  background: #e7ecee;
  margin: 0;
`;

export const SideCardWrapper = styled.div`
  display: flex;
  gap: 24px;

  ${media.MD`
    flex-direction: column;
  `}
`;

export const SideCard = styled.div<{ isFirst?: boolean }>`
  background: white;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 33px;
  flex: 1;
`;

export const SideCardTitle = styled.h3`
  font-family: "Roboto Slab", serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #333333;
  margin: 0;
`;

export const CountrySection = styled.div`
  width: 100%;
`;

export const CountryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2.4rem;
`;

export const CountryName = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 2.4rem;
  color: #f6921e;
`;

export const LinksList = styled.div`
  display: flex;
  margin-left: 2.4rem;
  flex-direction: column;
  gap: 8px;
`;

export const LinkItem = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  &::before {
    content: "";
    width: 4px;
    height: 4px;
    background: #1b75bb;
    border-radius: 50%;
  }
`;

export const LinkText = styled.span`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 2.4rem;
  color: #333333;
`;
